import React, { useState, useEffect } from 'react';
import { Mail, Send, Inbox, Archive, Filter, RefreshCw, Plus, AlertCircle, Clipboard, HelpCircle, Megaphone, Newspaper, Handshake, MessageCircle, Check } from 'lucide-react';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import MessageComposer from '../components/MessageComposer';
import MessageThread from '../components/MessageThread';
import styles from './LeadershipInbox.module.css';

const DEFAULT_AVATAR = 'https://storage.tficorg.org/DefaultIcon.png';

const getFullAvatarUrl = (avatarUrl: string | null | undefined): string => {
  if (!avatarUrl) return DEFAULT_AVATAR;
  if (avatarUrl.startsWith('http')) return avatarUrl;
  return `${process.env.REACT_APP_API_URL}${avatarUrl}`;
};

interface LeadershipMessage {
  id: number;
  senderId: number;
  senderName: string;
  senderAvatarUrl?: string;
  recipientId: number;
  recipientName: string;
  recipientAvatarUrl?: string;
  type: number;
  typeName: string;
  priority: number;
  priorityName: string;
  subject: string;
  content: string;
  status: number;
  statusName: string;
  readAt?: string;
  createdAt: string;
  parentMessageId?: number;
  replyCount: number;
  unreadReplyCount: number;
  lastActivityAt: string;
}

interface InboxStats {
  unreadCount: number;
  totalCount: number;
  briefingCount: number;
  requestCount: number;
  directiveCount: number;
  updateCount: number;
  collaborationCount: number;
}

const LeadershipInbox: React.FC = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<LeadershipMessage[]>([]);
  const [stats, setStats] = useState<InboxStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread' | 'archived'>('all');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<LeadershipMessage | null>(null);
  const [showComposer, setShowComposer] = useState(false);

  useEffect(() => {
    fetchMessages();
    fetchStats();
  }, [filter, typeFilter]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filter === 'unread') params.append('status', '0');
      if (filter === 'archived') params.append('status', '2');
      if (typeFilter) params.append('type', typeFilter);

      const response = await axios.get(`/LeadershipMessages/inbox?${params}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/LeadershipMessages/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const markAsRead = async (messageId: number) => {
    try {
      await axios.put(`/LeadershipMessages/${messageId}/read`);
      fetchMessages();
      fetchStats();
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const archiveMessage = async (messageId: number) => {
    try {
      await axios.put(`/LeadershipMessages/${messageId}/archive`);
      fetchMessages();
      fetchStats();
    } catch (error) {
      console.error('Error archiving message:', error);
    }
  };

  const getPriorityColor = (priority: number) => {
    switch (priority) {
      case 1: return '#3b82f6'; // Normal - Blue
      case 2: return '#f59e0b'; // High - Orange
      case 3: return '#ef4444'; // Urgent - Red
      default: return '#6b7280';
    }
  };

  const getTypeIcon = (typeName: string) => {
    const iconSize = 16;
    switch (typeName.toLowerCase()) {
      case 'briefing': return <Clipboard size={iconSize} />;
      case 'request': return <HelpCircle size={iconSize} />;
      case 'directive': return <Megaphone size={iconSize} />;
      case 'update': return <Newspaper size={iconSize} />;
      case 'collaboration': return <Handshake size={iconSize} />;
      default: return <Mail size={iconSize} />;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (hours < 24) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (hours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Mail size={32} />
          <div>
            <h1 className={styles.title}>Leadership Inbox</h1>
            <p className={styles.subtitle}>
              {stats && `${stats.unreadCount} unread of ${stats.totalCount} total messages`}
            </p>
          </div>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.btnPrimary} onClick={() => setShowComposer(true)}>
            <Plus size={18} />
            Compose
          </button>
          <button className={styles.btnSecondary} onClick={fetchMessages}>
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {stats && (
        <div className={styles.statsBar}>
          <div className={styles.statItem} onClick={() => setTypeFilter(null)}>
            <Inbox size={20} />
            <span>All ({stats.totalCount})</span>
          </div>
          <div className={styles.statItem} onClick={() => setTypeFilter('1')}>
            <Clipboard size={20} />
            <span>Briefings ({stats.briefingCount})</span>
          </div>
          <div className={styles.statItem} onClick={() => setTypeFilter('2')}>
            <HelpCircle size={20} />
            <span>Requests ({stats.requestCount})</span>
          </div>
          <div className={styles.statItem} onClick={() => setTypeFilter('3')}>
            <Megaphone size={20} />
            <span>Directives ({stats.directiveCount})</span>
          </div>
          <div className={styles.statItem} onClick={() => setTypeFilter('4')}>
            <Newspaper size={20} />
            <span>Updates ({stats.updateCount})</span>
          </div>
          <div className={styles.statItem} onClick={() => setTypeFilter('5')}>
            <Handshake size={20} />
            <span>Collaboration ({stats.collaborationCount})</span>
          </div>
        </div>
      )}

      <div className={styles.toolbar}>
        <div className={styles.statusFilters}>
          <button
            className={`${styles.statusBtn} ${filter === 'all' ? styles.active : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={`${styles.statusBtn} ${filter === 'unread' ? styles.active : ''}`}
            onClick={() => setFilter('unread')}
          >
            Unread
          </button>
          <button
            className={`${styles.statusBtn} ${filter === 'archived' ? styles.active : ''}`}
            onClick={() => setFilter('archived')}
          >
            <Archive size={16} />
            Archived
          </button>
        </div>
      </div>

      <div className={styles.messageList}>
        {loading ? (
          <div className={styles.loading}>Loading messages...</div>
        ) : messages.length === 0 ? (
          <div className={styles.emptyState}>
            <Mail size={48} />
            <p>No messages found</p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`${styles.messageItem} ${
                message.status === 0 || message.unreadReplyCount > 0 ? styles.unread : ''
              }`}
              onClick={() => setSelectedMessage(message)}
            >
              <div className={styles.messageIcon}>
                <img 
                  src={getFullAvatarUrl(
                    message.senderId === user?.id ? message.recipientAvatarUrl : message.senderAvatarUrl
                  )} 
                  alt={message.senderId === user?.id ? message.recipientName : message.senderName}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = DEFAULT_AVATAR;
                  }}
                />
              </div>

              <div className={styles.messageContent}>
                <div className={styles.messageHeader}>
                  <span className={styles.senderName}>
                    {message.senderId === user?.id ? `To: ${message.recipientName}` : message.senderName}
                  </span>
                  <span className={styles.messageDate}>{formatDate(message.lastActivityAt)}</span>
                </div>

                <div className={styles.messageSubject}>{message.subject}</div>
                
                <div className={styles.messageMeta}>
                  <span className={styles.messageType}>
                    {getTypeIcon(message.typeName)} {message.typeName}
                  </span>
                  {message.priority > 1 && (
                    <span 
                      className={styles.priorityBadge}
                      style={{ backgroundColor: getPriorityColor(message.priority) }}
                    >
                      {message.priorityName}
                    </span>
                  )}
                </div>

                {message.replyCount > 0 && (
                  <div className={styles.replyCount}>
                    <MessageCircle size={16} /> {message.replyCount} {message.replyCount === 1 ? 'reply' : 'replies'}
                    {message.unreadReplyCount > 0 && (
                      <span className={styles.unreadBadge}>
                        {message.unreadReplyCount} new
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className={styles.messageActions}>
                {message.status === 0 && (
                  <button
                    className={styles.actionBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      markAsRead(message.id);
                    }}
                    title="Mark as read"
                  >
                    <Check size={16} />
                  </button>
                )}
                {message.status !== 2 && (
                  <button
                    className={styles.actionBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      archiveMessage(message.id);
                    }}
                    title="Archive"
                  >
                    <Archive size={16} />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {showComposer && (
        <MessageComposer
          onClose={() => setShowComposer(false)}
          onSent={() => {
            fetchMessages();
            fetchStats();
          }}
        />
      )}

      {selectedMessage && (
        <MessageThread
          messageId={selectedMessage.id}
          currentUserId={user?.id}
          onClose={() => setSelectedMessage(null)}
          onUpdate={() => {
            fetchMessages();
            fetchStats();
          }}
        />
      )}
    </div>
  );
};

export default LeadershipInbox;
